import {
  Boxes,
  Calendar,
  ClipboardList,
  Factory,
  FileText,
  FolderKanban,
  Layers,
  Settings,
  ShoppingCart,
  Users,
  type LucideIcon,
} from "lucide-react"

// Office sidebar items + the permissions that make each one visible.
//
// `perms` lists permission names (see public.permissions) that grant the item;
// the user needs at least ONE of them (admins always pass — see
// useOfficePermissions). Each list mirrors the read grant the server enforces
// for the page's data (config/entities/*-entity-config.ts `rbac`), or the
// manage permission for admin/config pages, so a user only sees pages they can
// actually use. A group with children is shown when at least one child is.
export type OfficeNavSubItem = {
  title: string
  url: string
  perms?: string[]
}

export type OfficeNavItem = {
  title: string
  url: string
  icon: LucideIcon
  isActive?: boolean
  perms?: string[]
  items?: OfficeNavSubItem[]
}

export const OFFICE_NAV: OfficeNavItem[] = [
  {
    title: "Activities",
    url: "activities",
    icon: Calendar,
    isActive: true,
    perms: ["activities:read"],
    items: []
  },
  {
    title: "Templates",
    url: "/templates",
    icon: FileText,
    items: [
      {
        title: "Area Templates",
        url: "/templates/areas",
        perms: ["lots:read", "reference:manage"],
      },
      {
        title: "Activity Templates",
        url: "/templates/activities",
        perms: ["activities:read", "reference:manage"],
      }
    ]
  },
  {
    title: "Project",
    url: "#",
    icon: FolderKanban,
    isActive: true,
    items: [
      {
        title: "Builders",
        url: "builders",
        perms: ["jobs:read"],
      },
      // Projects View disabled - uncomment to re-enable
      // {
      //   title: "Projects View",
      //   url: "projects-view",
      // },
      {
        title: "Job View",
        url: "job-view",
        perms: ["jobs:read"],
      },
      {
        title: "Lots",
        url: "lots",
        perms: ["lots:read"],
      },
    ]
  },
  {
    title: "Active Job Tracker",
    url: "/job-tracker",
    icon: ClipboardList,
    isActive: true,
    perms: ["jobs:read"],
    items: []
  },
  {
    title: "Production",
    url: "#",
    icon: Factory,
    isActive: true,
    items: [
      {
        title: "Production Tracker",
        url: "/production-tracker",
        perms: ["activities:read"],
      },
      {
        title: "Production Overview",
        url: "/production-overview",
        perms: ["activities:read"],
      },
      {
        title: "Production Manager",
        url: "/production-manager",
        perms: ["activities:read"],
      }
    ]
  },
  {
    title: "Purchase Orders",
    url: "/purchase-orders",
    icon: ShoppingCart,
    items: [
      // Non-phase "Create PO" hidden by request — client only uses the
      // PO wizard. Route and page component left intact so deep
      // links keep working for engineers; restore this entry to re-expose.
      // {
      //   title: "Create PO",
      //   url: "/purchase-orders/create"
      // },
      {
        title: "New PO",
        url: "/purchase-orders/phase",
        perms: ["purchase_orders:create"],
      },
      {
        title: "PO Tracker",
        url: "/purchase-orders/tracker",
        perms: ["purchase_orders:read"],
      }
    ]
  },
  {
    title: "Inventory",
    url: "/inventory",
    icon: Boxes,
    items: [
      {
        title: "Inventory",
        url: "/inventory",
        perms: ["inventory:read"],
      },
      {
        title: "Activity",
        url: "/inventory/activity",
        perms: ["inventory:read"],
      },
    ]
  },
  {
    title: "Materials",
    url: "#",
    icon: Layers,
    isActive: true,
    items: [
      {
        title: "Materials",
        url: "/materials",
        perms: ["pricebooks:read", "pricebooks:manage", "reference:manage"],
      },
      {
        title: "Pricebooks",
        url: "/pricebooks",
        perms: ["pricebooks:read", "pricebooks:manage"],
      },
      {
        title: "Pricebook Ingestion",
        url: "/pricebooks/ingestion",
        perms: ["pricebooks:manage"],
      },
      {
        // Surfaced under Materials so the override settings live next
        // to the wizard that uses them. Page itself is routed under
        // /settings/project-config/ — the nav entry just promotes
        // it to a discoverable location.
        title: "Pricebook Mapping",
        url: "/settings/project-config/pricebook-material-mapping",
        perms: ["manage_pricebook_mapping", "pricebooks:manage"],
      },
      {
        // Each vendor's saved column mapping (vendor_import_configs).
        // Read-only unless the user can edit (card 5i7Hl2z2maGn).
        title: "Pricebook Import Mappings",
        url: "/settings/project-config/pricebook-import-mappings",
        perms: ["manage_pricebook_templates", "pricebooks:manage"],
      },
      {
        title: "Vendors",
        url: "/vendors",
        perms: ["pricebooks:read", "pricebooks:manage"],
      },
    ]
  },
  {
    // Single consolidated home for everything org/user related. Replaces the
    // former standalone top-level "User Management" and "Organization" items
    // plus the "System Settings > Organization & Users" child — those three
    // entry points all pointed at overlapping org/user pages. Children are the
    // canonical destinations; "Invite User" now lives on User Management.
    title: "Organization & Users",
    url: "/settings/organization",
    icon: Users,
    isActive: true,
    items: [
      {
        title: "User Management",
        url: "/settings/organization/user-management",
        perms: ["users:manage"],
      },
      {
        title: "Organization Details",
        url: "/settings/organization/details",
        perms: ["users:manage"],
      },
      {
        title: "Role Permissions",
        url: "/settings/organization/role-permissions",
        perms: ["roles:manage"],
      }
    ]
  },
  {
    title: "System Settings",
    url: "/settings",
    icon: Settings,
    items: [
      {
        title: "Project Configuration",
        url: "/settings/project-config",
        perms: ["settings:manage", "reference:manage"],
      },
      {
        title: "Import History",
        url: "/settings/imports",
        perms: ["pricebooks:manage"],
      }
    ]
  }
]

// Keep only the items the user may see. Items without `perms` are always
// shown; a group that had children but has none left is dropped (an empty
// group would just link to a page the user can't use).
export function filterNavByPermissions<T extends OfficeNavItem>(
  items: T[],
  canAny: (perms: string[]) => boolean,
): T[] {
  const allowed = (perms?: string[]) => !perms || perms.length === 0 || canAny(perms)
  return items.flatMap((item) => {
    if (!allowed(item.perms)) { return [] }
    if (!item.items?.length) { return [item] }
    const children = item.items.filter((child) => allowed(child.perms))
    return children.length ? [{ ...item, items: children }] : []
  })
}
